/**
 * Índice del visor: secciones, hojas, iconos y color (sin páginas ocultas).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink } from '../studioUi';
import { NavGlyph, accentHex, buildPageOutline, viewerPages } from './pageNav';

const ReportPageSidebar = ({ pages = [], sections = [], activePageId, onSelect }) => {
    const visible = useMemo(() => viewerPages(pages, sections), [pages, sections]);
    const outline = useMemo(() => buildPageOutline(visible, sections.filter((section) => !section.hidden)), [visible, sections]);
    const [collapsed, setCollapsed] = useState({});

    useEffect(() => {
        const current = pages.find((page) => page.id === activePageId);
        if (current?.sectionId) {
            setCollapsed((prev) => ({ ...prev, [current.sectionId]: false }));
        }
    }, [activePageId, pages]);

        if (visible.length < 2 && outline.filter((node) => node.type === 'page' || node.pages?.length).length < 2) return null;

    return (
        <nav style={ui.nav} aria-label="Hojas del reporte">
            {outline.map((node) => {
                if (node.type === 'section' && !node.pages.length) return null;
                if (node.type === 'page') {
                    return (
                        <SidebarPage
                            key={node.page.id}
                            page={node.page}
                            active={node.page.id === activePageId}
                            nested={false}
                            onSelect={onSelect}
                        />
                    );
                }
                const open = collapsed[node.section.id] !== true;
                const childActive = node.pages.some((page) => page.id === activePageId);
                const tone = accentHex(node.section.accent, ink.text);
                return (
                    <div key={node.section.id} style={ui.block}>
                        <button
                            type="button"
                            style={{
                                ...ui.section,
                                backgroundColor: childActive ? ink.wash : 'transparent',
                                color: tone,
                            }}
                            onClick={() => setCollapsed((prev) => ({ ...prev, [node.section.id]: open }))}
                        >
                            <span style={ui.caret}>{open ? '▾' : '▸'}</span>
                            <NavGlyph icon={node.section.icon} size={16} color={tone} />
                            <span style={ui.sectionLabel}>{node.section.name}</span>
                        </button>
                        {open && node.pages.map((page) => (
                            <SidebarPage
                                key={page.id}
                                page={page}
                                active={page.id === activePageId}
                                nested
                                onSelect={onSelect}
                            />
                        ))}
                    </div>
                );
            })}
        </nav>
    );
};

const SidebarPage = ({ page, active, nested, onSelect }) => {
    const tone = accentHex(page.accent, active ? ink.accent : ink.text);
    return (
        <button
            type="button"
            onClick={() => onSelect(page.id)}
            style={{
                ...ui.page,
                marginLeft: nested ? 18 : 0,
                backgroundColor: active ? ink.wash : 'transparent',
                color: tone,
                fontWeight: nested ? 500 : 800,
                textTransform: nested ? 'none' : 'uppercase',
                letterSpacing: nested ? 0 : '0.04em',
                fontSize: nested ? '0.82rem' : '0.72rem',
            }}
        >
            <NavGlyph icon={page.icon || 'page'} size={nested ? 14 : 15} color={tone} />
            <span style={ui.pageLabel}>{page.name}</span>
        </button>
    );
};

const ui = {
    nav: {
        width: 240,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '4px 8px 12px 0',
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        maxHeight: '100%',
    },
    block: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    section: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        borderWidth: 0,
        borderRadius: 20,
        padding: '8px 10px',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    caret: {
        width: 12,
        fontSize: '0.7rem',
        color: ink.muted,
        flexShrink: 0,
    },
    sectionLabel: {
        fontWeight: 800,
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    page: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        borderWidth: 0,
        borderRadius: 20,
        padding: '8px 12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    pageLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

export default ReportPageSidebar;
